import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session } from "../utils/session";
import { API_BASE_URL } from '../../config';
import UserAvatar from '../UserAvatar';

const authHeaders = () => {
  const token = Session.getToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  };
};

export default function MembersTable({ searchQuery = '' }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  

  

  const navigate = useNavigate();
  const role = Session.getRole();
  const isTeamLeader = role === "Team Leader";

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${API_BASE_URL}/api/Members/Users_List`, {
          headers: authHeaders(),
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();
        const normalized = Array.isArray(data)
          ? data.map((u) => ({
            ...u,
            UserId: u.userId ?? u.UserId,
            UserName: u.userName ?? u.UserName,
            FirstName: u.firstName ?? u.FirstName,
            LastName: u.lastName ?? u.LastName,
            Email: u.email ?? u.Email,
            PhoneNo: u.phoneNo ?? u.PhoneNo,
            UserRoleId: u.userRoleId ?? u.UserRoleId,
            CompaniesId: u.companiesId ?? u.CompaniesId,
            IsActive: u.isActive ?? u.IsActive ?? u.active ?? u.Active,
            TravellingUser: !!(u.travellingUser ?? u.TravellingUser),
          }))
          : [];
        setRows(normalized);
      } catch (err) {
        console.error('Error loading users:', err);
        setError(err.message || 'Something went wrong');
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/Members/delete/${userId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      if (!response.ok) {
        navigate('/Members');
      }
      setRows((prev) => prev.filter((u) => u.UserId !== userId));
      setSuccessMsg('Member deleted successfully');
      setTimeout(() => setSuccessMsg(''), 2000);
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  };

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const fullName = `${r.FirstName || ''} ${r.LastName || ''}`.trim().toLowerCase();
      const haystack = [
        r.UserName,
        fullName,
        r.Email,
        r.PhoneNo,
        r.roleName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [rows, searchQuery]);



  if (loading) {
    return (
      <div className="table-responsive">
        <div className="p-3 text-center text-muted">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="table-responsive">
        <div className="p-3 text-center text-danger">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      {successMsg && (
        <div
          className="alert alert-success text-center"
          style={{ marginBottom: '10px' }}
        >
          {successMsg}
        </div>
      )}

      <table className="table table-custom table-hover">
        <thead>
          <tr>
            <th>Roll</th>
            <th>Username</th>
            <th>Full name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Role Name</th>
            <th>Active</th>
            {!isTeamLeader && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {filteredRows.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center text-muted py-3">
                {searchQuery.trim() ? 'No members match your search.' : 'No users found.'}
              </td>
            </tr>
          )}
          {filteredRows.map((r, index) => (
            <tr key={r.UserId}>
              <td>{index + 1}</td>
              <td>
                <div className="employee-info">
                  <UserAvatar
                    userId={r.UserId}
                    userName={r.UserName}
                  />
                  <div className="emp-text">
                    <h6>{r.UserName}</h6>
                  </div>
                </div>
              </td>
              <td>{`${r.FirstName || ''} ${r.LastName || ''}`.trim()}</td>
              <td>{r.Email}</td>
              <td>{r.PhoneNo || ''}</td>
              <td>{r.roleName}</td>
              <td>{(r.IsActive ?? r.Active) ? 'Yes' : 'No'}</td>
              {!isTeamLeader && (
                <td>
                  <i
                    className="fas fa-edit text me-2"
                    style={{ cursor: 'pointer', color: '#0084fe' }}
                    onClick={() =>
                      navigate(`/Member/Edit/${r.UserId}`, { state: { user: r } })
                    }
                  />
                  <i
                    className="fas fa-trash text"
                    title="Delete"
                    style={{ cursor: 'pointer', color: '#0084fe' }}
                    onClick={() => handleDelete(r.UserId)}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
